export type NetworkSource = 'seed' | 'proxy' | 'peer' | 'unknown';
export type NetworkPath = 'direct' | 'turn' | 'unknown';
export type NetworkWaste = 'duplicate' | 'canceled' | 'invalid';

/** Media payload only, including background/failed transfers; excludes framing and protocol overhead. */
export interface NetworkTotals {
  payloadBytesReceived: number;
  bySource: Record<NetworkSource, number>;
  byPath: Record<NetworkPath, number>;
  duplicateBytes: number;
  canceledBytes: number;
  invalidBytes: number;
  knownInfrastructurePayloadBytes: number;
  unclassifiedPayloadBytes: number;
  infrastructurePayloadBytesPerWatchedHour: number | null;
  infrastructurePayloadBytesPerWatchedHourLowerBound: number | null;
}
export class NetworkAccounting {
  private readonly source: Record<NetworkSource, number> = { seed: 0, proxy: 0, peer: 0, unknown: 0 };
  private readonly path: Record<NetworkPath, number> = { direct: 0, turn: 0, unknown: 0 };
  private readonly waste: Record<NetworkWaste, number> = { duplicate: 0, canceled: 0, invalid: 0 };
  private bytes = 0;
  private infrastructure = 0;
  private unknown = 0;
  receive(source: NetworkSource, path: NetworkPath, bytes: number): void {
    if (!Number.isSafeInteger(bytes) || bytes <= 0) return;
    this.bytes += bytes;
    this.source[source] += bytes;
    this.path[path] += bytes;
    if (source === 'seed' || source === 'proxy' || path === 'turn') this.infrastructure += bytes;
    if (source === 'unknown' || (source === 'peer' && path === 'unknown')) this.unknown += bytes;
  }
  discard(kind: NetworkWaste, bytes: number): void {
    if (Number.isSafeInteger(bytes) && bytes > 0) this.waste[kind] += bytes;
  }
  snapshot(watchedMs: number): NetworkTotals {
    const perHour = watchedMs > 0 ? this.infrastructure * 3600000 / watchedMs : null;
    return {
      payloadBytesReceived: this.bytes, bySource: { ...this.source }, byPath: { ...this.path },
      duplicateBytes: this.waste.duplicate, canceledBytes: this.waste.canceled, invalidBytes: this.waste.invalid,
      knownInfrastructurePayloadBytes: this.infrastructure, unclassifiedPayloadBytes: this.unknown,
      infrastructurePayloadBytesPerWatchedHour: this.unknown ? null : perHour,
      infrastructurePayloadBytesPerWatchedHourLowerBound: perHour,
    };
  }
}
